import type { Language, Locale } from '@/config/locales';

import type { SplitPaymentSectionLabels } from '@/components/session/SplitPaymentSection';

/**
 * 분할결제 문구 (menu #814).
 *
 * ★ 상태 어휘(`pagado`/`en caja`/`pendiente`)는 서버가 내리는 분류와 **개념이 1:1** 이다.
 * 번역이 개념을 흐리면(예: `en caja` 를 "대기 중" 으로 뭉개면) 이미 카드를 건넨 손님이
 * 자기 몫을 또 내려 한다 — "카운터에서 처리 중" 이라는 뜻이 살아 있어야 한다.
 *
 * #522 — Locale 단일 출처 정합(15언어 전수). 키 누락 시 컴파일 에러로 강제.
 */
export type SplitPaymentTranslations =
  & Record<Language, SplitPaymentSectionLabels>
  & Record<Exclude<Locale, Language>, SplitPaymentSectionLabels>;

function make(t: {
  billsTitle: string;
  residual: string;
  myBill: string;
  claim: string;
  claiming: string;
  fullyClaimed: string;
  progressTitle: string;
  paid: string;
  inCashier: string;
  pending: string;
  unassigned: string;
  payRemaining: string;
  complete: string;
  claimConflict: string;
  claimFailed: string;
}): SplitPaymentSectionLabels {
  return {
    bills: {
      title: t.billsTitle,
      residual: t.residual,
      myBill: t.myBill,
      claim: t.claim,
      claiming: t.claiming,
      fullyClaimed: t.fullyClaimed,
    },
    progress: {
      title: t.progressTitle,
      paid: t.paid,
      inCashier: t.inCashier,
      pending: t.pending,
      unassigned: t.unassigned,
      payRemaining: t.payRemaining,
      complete: t.complete,
    },
    claimConflict: t.claimConflict,
    claimFailed: t.claimFailed,
  };
}

export const splitPaymentTranslations: SplitPaymentTranslations = {
  es: make({
    billsTitle: 'Cuentas de la mesa',
    residual: 'Sin asignar',
    myBill: 'Mía',
    claim: 'Tomar mi parte',
    claiming: 'Tomando…',
    fullyClaimed: 'Todo repartido',
    progressTitle: 'Estado del pago',
    paid: 'Pagado',
    inCashier: 'En caja',
    pending: 'Pendiente',
    unassigned: 'Sin dueño',
    payRemaining: 'Pagar lo que falta',
    complete: 'Cuenta saldada',
    claimConflict: 'Alguien tomó esa parte antes. Elige de nuevo.',
    claimFailed: 'No se pudo tomar la parte. Inténtalo otra vez.',
  }),
  ko: make({
    billsTitle: '테이블 계산서',
    residual: '남은 금액',
    myBill: '내 것',
    claim: '내 몫 가져오기',
    claiming: '가져오는 중…',
    fullyClaimed: '모두 나눔',
    progressTitle: '결제 진행',
    paid: '결제 완료',
    inCashier: '카운터 처리 중',
    pending: '미결제',
    unassigned: '주인 없음',
    payRemaining: '남은 금액 결제',
    complete: '결제 완료',
    claimConflict: '다른 분이 먼저 가져갔습니다. 다시 선택해 주세요.',
    claimFailed: '가져오지 못했습니다. 다시 시도해 주세요.',
  }),
  en: make({
    billsTitle: 'Table bills',
    residual: 'Unassigned',
    myBill: 'Mine',
    claim: 'Take my share',
    claiming: 'Taking…',
    fullyClaimed: 'All shared out',
    progressTitle: 'Payment status',
    paid: 'Paid',
    inCashier: 'At the counter',
    pending: 'Pending',
    unassigned: 'No owner',
    payRemaining: 'Pay what is left',
    complete: 'Bill settled',
    claimConflict: 'Someone took that share first. Please choose again.',
    claimFailed: 'Could not take that share. Please try again.',
  }),
  pt: make({
    billsTitle: 'Contas da mesa',
    residual: 'Não atribuído',
    myBill: 'Minha',
    claim: 'Pegar minha parte',
    claiming: 'Pegando…',
    fullyClaimed: 'Tudo dividido',
    progressTitle: 'Estado do pagamento',
    paid: 'Pago',
    inCashier: 'No caixa',
    pending: 'Pendente',
    unassigned: 'Sem dono',
    payRemaining: 'Pagar o que falta',
    complete: 'Conta quitada',
    claimConflict: 'Alguém pegou essa parte antes. Escolha de novo.',
    claimFailed: 'Não foi possível pegar a parte. Tente de novo.',
  }),
  fr: make({
    billsTitle: 'Additions de la table',
    residual: 'Non attribué',
    myBill: 'La mienne',
    claim: 'Prendre ma part',
    claiming: 'En cours…',
    fullyClaimed: 'Tout est réparti',
    progressTitle: 'État du paiement',
    paid: 'Payé',
    inCashier: 'En caisse',
    pending: 'En attente',
    unassigned: 'Sans propriétaire',
    payRemaining: 'Payer le reste',
    complete: 'Addition réglée',
    claimConflict: "Quelqu'un a pris cette part avant vous. Choisissez à nouveau.",
    claimFailed: 'Impossible de prendre cette part. Réessayez.',
  }),
  zh: make({
    billsTitle: '餐桌账单',
    residual: '未分配',
    myBill: '我的',
    claim: '认领我的份额',
    claiming: '认领中…',
    fullyClaimed: '已全部分完',
    progressTitle: '付款状态',
    paid: '已付款',
    inCashier: '收银台处理中',
    pending: '未付款',
    unassigned: '无归属',
    payRemaining: '支付剩余金额',
    complete: '账单已结清',
    claimConflict: '有人先认领了这份。请重新选择。',
    claimFailed: '认领失败，请重试。',
  }),
  ja: make({
    billsTitle: 'テーブルの伝票',
    residual: '未割り当て',
    myBill: '自分の分',
    claim: '自分の分を取る',
    claiming: '処理中…',
    fullyClaimed: 'すべて分割済み',
    progressTitle: '支払い状況',
    paid: '支払い済み',
    inCashier: 'レジで処理中',
    pending: '未払い',
    unassigned: '所有者なし',
    payRemaining: '残りを支払う',
    complete: 'お会計完了',
    claimConflict: '先に他の方が取りました。もう一度選んでください。',
    claimFailed: '取得できませんでした。もう一度お試しください。',
  }),
  hi: make({
    billsTitle: 'मेज़ के बिल',
    residual: 'अनिर्धारित',
    myBill: 'मेरा',
    claim: 'मेरा हिस्सा लें',
    claiming: 'लिया जा रहा है…',
    fullyClaimed: 'सब बँट गया',
    progressTitle: 'भुगतान की स्थिति',
    paid: 'भुगतान हो गया',
    inCashier: 'काउंटर पर',
    pending: 'बाकी है',
    unassigned: 'कोई मालिक नहीं',
    payRemaining: 'बचा हुआ भुगतान करें',
    complete: 'बिल चुकता',
    claimConflict: 'किसी और ने वह हिस्सा पहले ले लिया। फिर से चुनें।',
    claimFailed: 'हिस्सा नहीं लिया जा सका। फिर कोशिश करें।',
  }),
  de: make({
    billsTitle: 'Rechnungen am Tisch',
    residual: 'Nicht zugeordnet',
    myBill: 'Meine',
    claim: 'Meinen Anteil nehmen',
    claiming: 'Wird übernommen…',
    fullyClaimed: 'Alles aufgeteilt',
    progressTitle: 'Zahlungsstatus',
    paid: 'Bezahlt',
    inCashier: 'An der Kasse',
    pending: 'Offen',
    unassigned: 'Ohne Zuordnung',
    payRemaining: 'Rest bezahlen',
    complete: 'Rechnung beglichen',
    claimConflict: 'Jemand war schneller. Bitte neu auswählen.',
    claimFailed: 'Anteil konnte nicht übernommen werden. Bitte erneut versuchen.',
  }),
  it: make({
    billsTitle: 'Conti del tavolo',
    residual: 'Non assegnato',
    myBill: 'Il mio',
    claim: 'Prendi la mia parte',
    claiming: 'In corso…',
    fullyClaimed: 'Tutto diviso',
    progressTitle: 'Stato del pagamento',
    paid: 'Pagato',
    inCashier: 'Alla cassa',
    pending: 'In sospeso',
    unassigned: 'Senza proprietario',
    payRemaining: 'Paga il resto',
    complete: 'Conto saldato',
    claimConflict: "Qualcuno ha preso quella parte prima. Scegli di nuovo.",
    claimFailed: 'Non è stato possibile prendere la parte. Riprova.',
  }),
  ru: make({
    billsTitle: 'Счета стола',
    residual: 'Не распределено',
    myBill: 'Мой',
    claim: 'Взять свою часть',
    claiming: 'Выполняется…',
    fullyClaimed: 'Всё распределено',
    progressTitle: 'Статус оплаты',
    paid: 'Оплачено',
    inCashier: 'На кассе',
    pending: 'Не оплачено',
    unassigned: 'Без владельца',
    payRemaining: 'Оплатить остаток',
    complete: 'Счёт закрыт',
    claimConflict: 'Эту часть уже взяли. Выберите другую.',
    claimFailed: 'Не удалось взять часть. Попробуйте ещё раз.',
  }),
  ar: make({
    billsTitle: 'فواتير الطاولة',
    residual: 'غير مُخصَّص',
    myBill: 'فاتورتي',
    claim: 'خُذ حصتي',
    claiming: 'جارٍ الأخذ…',
    fullyClaimed: 'تم التقسيم بالكامل',
    progressTitle: 'حالة الدفع',
    paid: 'مدفوع',
    inCashier: 'لدى الكاشير',
    pending: 'غير مدفوع',
    unassigned: 'بلا مالك',
    payRemaining: 'ادفع المتبقي',
    complete: 'تمت تسوية الفاتورة',
    claimConflict: 'أخذ شخص آخر هذه الحصة قبلك. اختر مرة أخرى.',
    claimFailed: 'تعذّر أخذ الحصة. حاول مرة أخرى.',
  }),
  vi: make({
    billsTitle: 'Hoá đơn của bàn',
    residual: 'Chưa phân bổ',
    myBill: 'Của tôi',
    claim: 'Nhận phần của tôi',
    claiming: 'Đang nhận…',
    fullyClaimed: 'Đã chia hết',
    progressTitle: 'Trạng thái thanh toán',
    paid: 'Đã thanh toán',
    inCashier: 'Đang ở quầy',
    pending: 'Chưa thanh toán',
    unassigned: 'Chưa có chủ',
    payRemaining: 'Trả phần còn lại',
    complete: 'Đã thanh toán xong',
    claimConflict: 'Người khác đã nhận phần đó trước. Vui lòng chọn lại.',
    claimFailed: 'Không nhận được phần này. Vui lòng thử lại.',
  }),
  th: make({
    billsTitle: 'บิลของโต๊ะ',
    residual: 'ยังไม่ได้แบ่ง',
    myBill: 'ของฉัน',
    claim: 'รับส่วนของฉัน',
    claiming: 'กำลังดำเนินการ…',
    fullyClaimed: 'แบ่งครบแล้ว',
    progressTitle: 'สถานะการชำระเงิน',
    paid: 'ชำระแล้ว',
    inCashier: 'อยู่ที่แคชเชียร์',
    pending: 'ยังไม่ชำระ',
    unassigned: 'ไม่มีเจ้าของ',
    payRemaining: 'ชำระส่วนที่เหลือ',
    complete: 'ชำระครบแล้ว',
    claimConflict: 'มีคนรับส่วนนั้นไปก่อนแล้ว กรุณาเลือกใหม่',
    claimFailed: 'รับส่วนนี้ไม่สำเร็จ กรุณาลองใหม่',
  }),
  nl: make({
    billsTitle: 'Rekeningen van de tafel',
    residual: 'Niet toegewezen',
    myBill: 'Van mij',
    claim: 'Mijn deel nemen',
    claiming: 'Bezig…',
    fullyClaimed: 'Alles verdeeld',
    progressTitle: 'Betaalstatus',
    paid: 'Betaald',
    inCashier: 'Bij de kassa',
    pending: 'Openstaand',
    unassigned: 'Geen eigenaar',
    payRemaining: 'Rest betalen',
    complete: 'Rekening voldaan',
    claimConflict: 'Iemand was je voor. Kies opnieuw.',
    claimFailed: 'Kon dat deel niet nemen. Probeer het opnieuw.',
  }),
};

export function getSplitPaymentLabels(
  locale: Locale | null | undefined,
  fallback: Locale = 'es'
): SplitPaymentSectionLabels {
  return splitPaymentTranslations[locale ?? fallback] ?? splitPaymentTranslations[fallback];
}
